import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Row } from 'react-bootstrap';
import StyledSelect from '../forms/styledSelect';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from '../../utils/toast';
import DatePicker from '../../components/forms/formDatePicker';
import ClientInfoModal from '../../pages/clients/clientInfo';
import SetClientModal from '../../pages/clients/clientsSet';
import InlineAddressMap from './inlineAddressMap';
import { getAvailableBadges } from '../../services/badges';
import { tagLabel, ASSIGNMENT_LABELS } from '../../constants/labels';
import { getUsersAsOptions } from '../../services/companyUsers';
import { getClientStats } from '../../services/clients';
import { createJob } from '../../services/jobs';
import { intlPhoneDigits, openViberChat, roundDecimal, rsdFormat, toLocalDateString, getFullAddress } from '../../utils/helper';
import Avatar from '../avatar';
import { isCleaningAndOnwards, isCleaningCompletedAndOnwards, validateJobForm } from '../../utils/jobHelper';
import { onQuerySuccess } from '../../utils/queryResponse';
import { invalidateJobStats } from '../../utils/invalidateStats';
import FormInput from '../forms/formInput';
import OverlayLoader from '../overlayLoader';
import AssigneeSelect from '../selects/assigneeSelect';
import BadgeSelect from '../selects/badgeSelect';
import ClientsSelect from '../selects/clientsSelect';
import ServiceSelect from '../selects/serviceSelect';
import MaterialSelect from '../selects/materialSelect';
import StatusSelector from '../statusSelector';
import { JOB_STATUS, getStatusLabel, getStatusIndex, normalizeStatus } from '../../constants/jobStatus';
import SbrDrawer from '../sbr/SbrDrawer';
import ViberIcon from '../icons/viberIcon';
import useIsMobile from '../../hooks/useIsMobile';

/** First tag picked on more than one item of the form (label), or null. Tags are single-use. */
const findDuplicateTag = (lineItems, labelOf) => {
    const seen = new Set()
    for (const li of lineItems || []) {
        if (!li.badgeId) continue
        if (seen.has(li.badgeId)) return labelOf(li.badgeId) || li.badgeId
        seen.add(li.badgeId)
    }
    return null
}


const BoxIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
);
const MaterialIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
);
const ServiceIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
);
const initJob = {
    clientId: '',
    pickupType: 'PICKUP_FROM_HOME',
    pickupTime: null,
    pickupTimeTo: null,
    status: 'NEW',
    dispatchTime: null,
    completionTime: null,
    completionTimeTo: null,
    paymentStatus: 'PENDING',
    paymentMethod: null,
    lineItemsTemp: [],
    paymentType: null,
    paymentTime: null,
    pickupAssignedToId: null,
    cleaningAssignedToId: null,
    deliveryAssignedToId: null,
    note: ''
}

const SetOrderModal = ({
    display,
    toggle,
    pickupTime,
    clientId
}) => {

    const { t } = useTranslation()
    const PICKUP_LBL = { PICKUP_FROM_HOME: t('orderForm.pickupFromHomeLabel'), CUSTOMER_DELIVERS: t('orderForm.customerDeliversLabel') }

    const [setClientModal, setSetClientModal] = useState(false)
    const [clientInfoModal, setClientInfoModal] = useState(false)

    const [isAdvance, setIsAdvance] = useState(true)

    const [clientRawName, setClientRawName] = useState('')
    const [clientOptions, setClientOptions] = useState([])
    const [assigneesOptions, setAssigneesOptions] = useState([])
    const [availableBadges, setAvailableBadges] = useState([])
    const [selectedBadges, setSelectedBadges] = useState([])

    const [job, setJob] = useState(initJob)

    // index of the line item open in the right detail pane (null = show summary)
    const [detailIndex, setDetailIndex] = useState(null)
    const openItem = (index) => setDetailIndex(index)
    const closeItem = () => setDetailIndex(null)

    // mobile wizard
    const isMobile = useIsMobile()
    const WIZ_STEPS = [t('orderForm.stepClient'), t('orderForm.stepItems'), t('orderForm.stepStatus'), t('orderForm.stepTeam'), t('orderForm.stepSchedule'), t('orderForm.stepReview')]
    const [wizStep, setWizStep] = useState(0)
    const goWiz = (delta) => setWizStep((s) => Math.max(0, Math.min(WIZ_STEPS.length - 1, s + delta)))

    const onShow = () => {
        setDetailIndex(null)
        setWizStep(0)
        setJob({
            ...initJob,
            ...(pickupTime ? { pickupTime } : {}),
            ...(clientId ? { clientId } : {}),
        })
    }


    const { data: dbAssigneesOptions, isLoading: isLoadingAssigneesOptions } = useQuery(['users-option'], () => getUsersAsOptions())
    const { data: dbAvailableBadges, isLoading: isAvailableBadgesLoading, isFetching: isAvailableBadgesFetching } = useQuery(['available-badges'], () => getAvailableBadges())

    // Live client summary card (Step 1) — reuses the client dashboard stats endpoint.
    const { data: dbClientStats, isFetching: isClientStatsFetching } = useQuery(
        ['client-stats', job.clientId],
        () => getClientStats(job.clientId),
        { enabled: !!job.clientId, staleTime: 1000 * 60 }
    )
    const clientStats = dbClientStats?.code === 200 ? dbClientStats.message : null

    useEffect(() => {

        if (!dbAssigneesOptions || !dbAvailableBadges)
            return

        onQuerySuccess(dbAssigneesOptions, (result) => {
            if (result.length > 0) {
                setAssigneesOptions(result.map(d => ({
                    value: d.id, label: d.fullName, roles: d.roles, avatarUrl: d.avatarUrl
                })))
            }
        })

        onQuerySuccess(dbAvailableBadges, (result) => {
            if (result.length > 0) {
                setAvailableBadges(result.map(d => ({
                    value: d.id, label: tagLabel(d)
                })))
            }
        })

    }, [dbAssigneesOptions, dbAvailableBadges])

    const onHide = () => {
        setIsAdvance(false)
        setJob({ ...initJob, lineItemsTemp: [] })
        toggle(false)
    }

    const queryClient = useQueryClient()

    const { mutate, isLoading } = useMutation(createJob, {
        onSuccess: (response, payload) => {
            const { code, message: responseMessage } = response

            if (code !== 200) {
                if (code === 403) {
                    toast.error(t('orderForm.notAllowed'), {
                        position: toast.POSITION.BOTTOM_RIGHT
                    })
                    return
                }
                toast.error(responseMessage, {
                    position: toast.POSITION.BOTTOM_RIGHT
                })
                queryClient.invalidateQueries(['available-badges'])
                return
            }

            invalidateJobStats(queryClient)
            queryClient.invalidateQueries(['available-badges'])
            setJob({ ...initJob, lineItemsTemp: [] })
            onHide()
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()

        const validationError = validateJobForm(job)
        if (validationError) {
            toast.error(validationError, { position: toast.POSITION.BOTTOM_RIGHT })
            return
        }

        const duplicateTag = findDuplicateTag(job.lineItemsTemp, badgeLabel)
        if (duplicateTag) {
            toast.error(t('orderForm.duplicateTag', { tag: duplicateTag }), { position: toast.POSITION.BOTTOM_RIGHT })
            return
        }

        const _job = { ...job }

        if (normalizeStatus(_job.status) === JOB_STATUS.READY && !_job.dispatchTime)
            _job.dispatchTime = new Date().toISOString()

        _job.price = parseFloat(getTotalPriceRaw())
        _job.lineItems = _job.lineItemsTemp.map((x => {
            const isMat = x.type === 'MATERIAL'
            return {
                type: x.type || 'SERVICE',
                quantity: x.quantity,
                price: x.price,
                taxRate: x.taxRate,
                taxAmount: x.taxAmount,
                priceWithTax: x.priceWithTax,
                serviceId: isMat ? undefined : x.serviceId,
                materialId: isMat ? x.materialId : undefined,
                badgeId: x.badgeId,
                unit: x.unit,
                length: x.unit === 'm2' ? x.length : undefined,
                width: x.unit === 'm2' ? x.width : undefined,
            }
        }))

        mutate(_job)
    }

    const handleChange = (objValue) => {

        const newOrder = { ...job, ...objValue }
        if (newOrder.pickupType === "CUSTOMER_DELIVERS") {
            newOrder.pickupTime = null
            newOrder.pickupTimeTo = null
        }
        if (newOrder.pickupType === "PICKUP_FROM_HOME") {
            newOrder.deliveredByCustomerTime = null
            newOrder.deliveredByCustomerTimeTo = null
        }

        setJob(newOrder)
    }

    const handleAddLineItem = () => {
        let _order = job
        let _lineItems = job.lineItemsTemp
        // Tags are single-use, so a new item never inherits one: the editor
        // offers the next free tag as a one-click chip instead.
        const badgeId = ''
        _lineItems.push({
            type: 'SERVICE',
            quantity: 0,
            price: 0,
            taxRate: 0,
            taxAmount: 0,
            priceWithTax: 0,
            serviceId: '',
            badgeId
        })
        _order.lineItemsTemp = _lineItems
        setJob({ ..._order })
        setSelectedBadges(_lineItems.map(x => x.badgeId))
        // open the freshly added item in the right detail pane
        openItem(_lineItems.length - 1)
    }

    const handleRemoveLineItem = (index) => {
        let _order = job
        let _lineItems = job.lineItemsTemp
        _lineItems.splice(index, 1)
        _order.lineItemsTemp = _lineItems

        const _selectedBadges = _lineItems.map(x => x.badgeId)
        setSelectedBadges(_selectedBadges)

        setJob({ ..._order })
        closeItem()
    }

    const handleLineItemChange = (objValue, index) => {

        const _lineItems = [...job.lineItemsTemp]; // Create a new copy of the array
        _lineItems[index] = { ..._lineItems[index], ...objValue }

        let _quantity = _lineItems[index].quantity

        if (_lineItems[index].unit === 'm2') {
            const length = +_lineItems[index].length || 0
            const width = +_lineItems[index].width || 0
            _quantity = roundDecimal(length * width, 2)
        }

        _lineItems[index].quantity = _quantity

        let _price = +_lineItems[index].pricePerUnit * +_lineItems[index].quantity
        _lineItems[index].price = roundDecimal(_price, 2)

        let _taxAmount = (+_lineItems[index].taxRate / 100) * +_lineItems[index].price
        _lineItems[index].taxAmount = roundDecimal(_taxAmount, 2)

        let _priceWithTax = +_lineItems[index].price + +_lineItems[index].taxAmount
        _lineItems[index].priceWithTax = roundDecimal(_priceWithTax, 2)

        const _selectedBadges = _lineItems.map(x => x.badgeId)
        setSelectedBadges(_selectedBadges)

        setJob((prevJob) => ({
            ...prevJob,
            lineItemsTemp: _lineItems // Update the state with the new array
        }));
    }

    const getTotalPriceRaw = () => {
        var sum = job.lineItemsTemp?.reduce(function (acc, obj) {
            return parseFloat(acc) + parseFloat(obj.priceWithTax);
        }, 0);
        return sum;
    }

    const getTotalPrice = () => {
        var sum = job.lineItemsTemp?.reduce(function (acc, obj) {
            return parseFloat(acc) + parseFloat(obj.priceWithTax);
        }, 0);
        return rsdFormat(sum)
    }

    const onCreateClient = (newClient) => {

        let _clientOptions = clientOptions
        _clientOptions.push({
            value: newClient.id,
            label: newClient.name
        })

        let _job = job
        _job.clientId = newClient.id
        setJob({ ..._job })
    }

    const handleSelectDate = (prop, date) => {
        let _date = date

        const dateObj = new Date(date)
        //check if midnight, means no time is selected then set to 8:00 as default
        if (dateObj.getHours() === 0 && dateObj.getMinutes() === 0 && dateObj.getSeconds() === 0) {
            _date = setHours(setMinutes(date, 0), 8)
        }

        handleChange({ [prop]: _date })
    }

    const handlePaymentStatusChange = (checked) => {
        handleChange({ paymentStatus: checked ? 'PAID' : 'PENDING' })
    }

    const handleServiceSelect = (option, index) => {
        handleLineItemChange({ serviceId: option.value, serviceName: option.label, pricePerUnit: parseFloat(option.ppu), unit: option.unit, quantity: 0 }, index)
    }

    const handleMaterialSelect = (option, index) => {
        handleLineItemChange({
            materialId: option.value,
            materialName: option.label,
            pricePerUnit: parseFloat(option.ppu),
            unit: option.unit,
            taxRate: parseFloat(option.taxRate) || 0,
            quantity: 1,
        }, index)
    }

    const handleAddMaterialLineItem = () => {
        let _order = job
        let _lineItems = job.lineItemsTemp
        _lineItems.push({
            type: 'MATERIAL',
            quantity: 1,
            price: 0,
            taxRate: 0,
            taxAmount: 0,
            priceWithTax: 0,
            materialId: '',
            badgeId: ''
        })
        _order.lineItemsTemp = _lineItems
        setJob({ ..._order })
        setSelectedBadges(_lineItems.map(x => x.badgeId))
        openItem(_lineItems.length - 1)
    }

    const getLineItemMinQuantity = (lineItemUnit) => {

        if (isCleaningCompletedAndOnwards(job)) {
            return lineItemUnit === 'Piece' ? 1 : 0.01
        } else {
            return 0
        }
    }

    const badgeLabel = (badgeId) => availableBadges.find(b => b.value === badgeId)?.label
    const sortBadgeOptions = (options) => [...options].sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }))
    // The lowest free tag not already picked on this form — the one-click "Next" suggestion.
    const nextFreeBadge = sortBadgeOptions(availableBadges).find(b => !selectedBadges.includes(b.value))
    const renderNextTagChip = (lineItem, index, disabled) => (nextFreeBadge && lineItem.badgeId !== nextFreeBadge.value && !disabled) ? (
        <button type="button" className="rf" title={t('orderForm.useNextFreeTag')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px', padding: '2px 8px', borderRadius: '12px', border: '1px solid var(--brand-100)', background: 'var(--brand-50)', color: 'var(--brand-ink)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => handleLineItemChange({ badgeId: nextFreeBadge.value }, index)}>
            <i className="fa-solid fa-tag"></i> {t('orderForm.nextTag', { label: nextFreeBadge.label })}
        </button>
    ) : null

    /* compact, clickable line-item card (left column) */
    const renderItemCard = (lineItem, index) => {
        const isOn = detailIndex === index
        const isMaterial = lineItem.type === 'MATERIAL'
        const name = isMaterial
            ? (lineItem.materialName || lineItem.material?.name || (lineItem.materialId ? t('orderForm.selectedMaterial') : null))
            : (lineItem.serviceName || lineItem.service?.name || (lineItem.serviceId ? t('orderForm.selectedService') : null))
        const bl = badgeLabel(lineItem.badgeId)
        const meta = (isMaterial ? lineItem.materialId : lineItem.serviceId)
            ? `${rsdFormat(+lineItem.quantity, false)} ${lineItem.unit || ''}${bl ? ` · ${t('orderForm.tag')} ${bl}` : ''}`
            : (isMaterial ? t('orderForm.tapToChooseMaterial') : t('orderForm.tapToChooseService'))
        return (
            <button type="button" key={`li-pick-${index}`} className={`li-pick${isOn ? ' on' : ''}`} onClick={() => openItem(index)}>
                <span className="li-pick-ic">{isMaterial ? <MaterialIcon /> : <ServiceIcon />}</span>
                <span className="li-pick-mid">
                    <span className={`li-pick-nm${name ? '' : ' ph'}`}>{name || t('orderForm.itemN', { n: index + 1 })}</span>
                    <span className="li-pick-meta">{meta}</span>
                </span>
                <span className="li-pick-tot">{rsdFormat(+lineItem.priceWithTax, false)}</span>
                <span className="li-pick-chev">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </span>
            </button>
        )
    }

    /* right column — totals + status summary */
    const renderSummary = () => (
        <div className="job-pane summary" key="summary">
            <div className="job-sum-ttl">{t('orderForm.summary')}</div>
            <Form.Label>{t('common.status')} <span className="text-danger">*</span></Form.Label>
            <StatusSelector value={job.status} onChange={(status) => handleChange({ status: status })} />
            <div className="job-totals">
                <div className="tl"><span>{t('orderForm.items')}</span><span>{job.lineItemsTemp?.length || 0}</span></div>
                <div className="grand"><span className="l">{t('orderForm.grandTotal')}</span><span className="v">{getTotalPrice()}</span></div>
            </div>
        </div>
    )

    /* right column — single line-item detail editor */
    const renderItemEditor = (index) => {
        const lineItem = job.lineItemsTemp?.[index]
        if (!lineItem) return null
        const isMaterial = lineItem.type === 'MATERIAL'
        return (
            <div className="job-pane detail li-detail" key={`detail-${index}`}>
                <div className="li-detail-head">
                    <button type="button" className="li-detail-back" title={t('orderForm.backToSummary')} onClick={closeItem}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div className="tx">
                        <div className="t">{isMaterial ? t('orderForm.material') : t('orderForm.lineItemN', { n: index + 1 })}</div>
                        <div className="s">{isMaterial ? t('orderForm.editMaterialAndPricing') : t('orderForm.editServiceAndPricing')}</div>
                    </div>
                </div>
                <div className="li-grid">
                    <div className="li-col-2">
                        {isMaterial ? (
                            <Form.Group>
                                <Form.Label>{t('orderForm.material')} <span className="text-danger">*</span></Form.Label>
                                <MaterialSelect required value={lineItem.materialId ?? ''} onChange={(option) => handleMaterialSelect(option, index)} />
                            </Form.Group>
                        ) : (
                            <Form.Group>
                                <Form.Label>{t('orderForm.service')} <span className="text-danger">*</span></Form.Label>
                                <ServiceSelect required value={lineItem.serviceId ?? ''} onChange={(option) => handleServiceSelect(option, index)} />
                            </Form.Group>
                        )}
                    </div>
                    <div className="li-col-2">
                        <Form.Group>
                            <Form.Label>{t('orderForm.tagNumber')} {isCleaningAndOnwards(job) && <span className="text-danger">*</span>}</Form.Label>
                            <BadgeSelect required={isCleaningAndOnwards(job)} value={lineItem.badgeId ?? ''}
                                isLoading={isAvailableBadgesLoading}
                                options={sortBadgeOptions((availableBadges).filter(x => !selectedBadges.includes(x.value) || x.value === lineItem.badgeId))}
                                onChange={(option) => handleLineItemChange({ badgeId: option.value }, index)} />
                            {renderNextTagChip(lineItem, index)}
                        </Form.Group>
                    </div>
                    {lineItem.unit === 'm2' &&
                        <>
                            <div>
                                <FormInput value={lineItem.width ?? ''} label={t('orderForm.width')} type="number" min={0.01} step="0.01"
                                    onChange={(e) => handleLineItemChange({ width: e.target.value }, index)}
                                    required={isCleaningCompletedAndOnwards(job)} />
                            </div>
                            <div>
                                <FormInput value={lineItem.length ?? ''} label={t('orderForm.length')} type="number" min={0.01} step="0.01"
                                    onChange={(e) => handleLineItemChange({ length: e.target.value }, index)}
                                    required={isCleaningCompletedAndOnwards(job)} />
                            </div>
                        </>
                    }
                    <div>
                        <FormInput value={lineItem.quantity} title={rsdFormat(+lineItem.quantity, false)}
                            label={(lineItem.serviceId || lineItem.materialId) ? lineItem.unit : t('orderForm.quantity')}
                            type="number" min={getLineItemMinQuantity(lineItem.unit)} step={lineItem.unit === 'Piece' ? 1 : 0.01}
                            onChange={(e) => handleLineItemChange({ quantity: e.target.value }, index)}
                            disabled={lineItem.unit === 'm2'} required />
                    </div>
                    <div>
                        <FormInput value={lineItem.taxRate} label={t('orderForm.taxPercent')} type="number" step='0.01'
                            onChange={(e) => handleLineItemChange({ taxRate: e.target.value }, index)} />
                    </div>
                    <div>
                        <FormInput disabled value={lineItem.price} title={rsdFormat(+lineItem.price, false)}
                            label={t('orderForm.price')} type="number" min="0.01" step="0.01"
                            onChange={(e) => handleLineItemChange({ price: e.target.value }, index)} required />
                    </div>
                    <div>
                        <FormInput disabled value={lineItem.priceWithTax} title={rsdFormat(+lineItem.priceWithTax, false)}
                            label={t('orderForm.total')} type="number" min="0.01" step="0.01" required
                            onChange={(e) => handleLineItemChange({ priceWithTax: e.target.value }, index)} />
                    </div>
                </div>
                <div className="li-detail-total">
                    <span className="l">{t('orderForm.lineTotal')}</span>
                    <span className="v">{rsdFormat(+lineItem.priceWithTax, false)} RSD</span>
                </div>
                <button type="button" className="li-detail-remove" onClick={() => handleRemoveLineItem(index)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                    {t('orderForm.removeItem')}
                </button>
            </div>
        )
    }

    /* ── field groups (shared by desktop split + mobile wizard) ── */
    /* Step 1 — live client summary card (mockup: "show client summary after selection") */
    const renderClientSummary = () => {
        if (!job.clientId) return null
        if (isClientStatsFetching && !clientStats) return <Col xs="12"><div className="jwc-card jwc-skel">{t('orderForm.loadingClient')}</div></Col>
        if (!clientStats) return null
        const c = clientStats.client
        const isCompany = c.clientType === 'COMPANY'
        const contactName = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()
        const name = isCompany ? (c.companyName || contactName) : contactName
        const phoneDigits = intlPhoneDigits(c.phone)
        const address = getFullAddress(c)
        return (
            <Col xs="12">
                <div className="jwc-card">
                    <div className="jwc-top">
                        <Avatar square size={44} name={name} src={c.avatarUrl} />
                        <div className="jwc-id">
                            <div className="jwc-nm">{name}<span className="jwc-badge">{isCompany ? t('orderForm.company') : t('orderForm.individual')}</span><span className="jwc-badge">{clientStats.totalJobs > 0 ? t('orderForm.active') : t('orderForm.new')}</span></div>
                            {isCompany && contactName && <div className="jwc-phone">{t('orderForm.contact')} {contactName}</div>}
                            {c.phone && <div className="jwc-phone">{c.phone}</div>}
                        </div>
                        <div className="jwc-actions">
                            {phoneDigits &&
                                <a className="jwc-ic wa" href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noreferrer" title="WhatsApp">
                                    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.94 11.94 0 0 0 5.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.86 11.86 0 0 0-3.481-8.408" /></svg>
                                </a>}
                            {phoneDigits &&
                                <a className="jwc-ic vb" href={`viber://chat/?number=%2B${phoneDigits}`} onClick={(e) => { e.preventDefault(); openViberChat(c.phone); }} title="Viber">
                                    <ViberIcon />
                                </a>}
                            {c.phone &&
                                <a className="jwc-ic" href={`tel:${c.phone}`} title={t('clients.call')}>
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                                </a>}
                        </div>
                    </div>
                    {/* Address text + inline map strip — the location is part of
                        the card itself, not hidden behind the modal. */}
                    <InlineAddressMap address={address} clientId={c.id} latitude={c.latitude} longitude={c.longitude} />
                    <div className="jwc-stats">
                        <div className="jwc-stat"><div className="l">{t('orderForm.totalJobs')}</div><div className="v">{clientStats.totalJobs}</div></div>
                        <div className="jwc-stat"><div className="l">{t('orderForm.lastJob')}</div><div className="v">{clientStats.lastJob ? toLocalDateString(clientStats.lastJob.date, 'dd.MM.yyyy') : '—'}</div></div>
                        <div className="jwc-stat"><div className="l">{t('orderForm.lifetimeValue')}</div><div className="v">{rsdFormat(clientStats.totalRevenue)}</div></div>
                    </div>
                    <div className="jwc-foot">
                        <div className={`jwc-out${clientStats.unpaidAmount > 0 ? ' due' : ''}`}>
                            <span className="l">{t('orderForm.outstanding')}</span><span className="v">{rsdFormat(clientStats.unpaidAmount)}</span>
                        </div>
                        <button type="button" className="jwc-view" onClick={() => setClientInfoModal(true)}>{t('orderForm.viewClient')}</button>
                    </div>
                </div>
            </Col>
        )
    }

    const fClient = () => (
        <Col xs="12">
            <Row className="mb-2">
                <Col lg="12">
                    <div className="d-flex justify-content-between">
                        <Form.Label>{t('orderForm.client')} <span className="text-danger">*</span></Form.Label>
                        <div>
                            <span className="me-2 text-muted"><small>{t('orderForm.clientNotFound')}</small></span>
                            <Button style={{ fontSize: '0.70rem' }} className="mb-1 py-0 px-2" size="sm" variant="light" onClick={() => setSetClientModal(true)}><i className="fas fa-plus"></i> {t('orderForm.addNew')}</Button>
                        </div>
                    </div>
                    <div>
                        <div className="d-flex">
                            <div style={{ width: '90%' }}>
                                <ClientsSelect value={job.clientId} onChange={(option) => handleChange({ clientId: option.value })} options={clientOptions} setOptions={setClientOptions} setClientRawName={setClientRawName} />
                            </div>
                            {job.clientId &&
                                <div style={{ width: '10%' }}>
                                    <div className="d-flex center-xy">
                                        <i title={t('orderForm.viewClientDetails')} onClick={() => setClientInfoModal(true)} className="fa-solid fa-circle-info text-muted cursor-pointer"></i>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </Col>
            </Row>
        </Col>
    )

    const fPickupType = () => (
        <Col lg="6">
            <Form.Group className="mb-2">
                <Form.Label>{t('orderForm.pickupType')} <span className="text-danger">*</span></Form.Label>
                <StyledSelect value={job.pickupType} onChange={(e) => handleChange({ pickupType: e.target.value })} >
                    <option value="" disabled>{t('orderForm.selectPickupType')}</option>
                    <option value="PICKUP_FROM_HOME">{t('orderForm.pickupFromHome')}</option>
                    <option value="CUSTOMER_DELIVERS">{t('orderForm.customerDelivers')}</option>
                </StyledSelect>
            </Form.Group>
        </Col>
    )

    const fPickupTime = () => {
        const isCustomerDelivers = job.pickupType === "CUSTOMER_DELIVERS"
        const fromField = isCustomerDelivers ? 'deliveredByCustomerTime' : 'pickupTime'
        const toField = isCustomerDelivers ? 'deliveredByCustomerTimeTo' : 'pickupTimeTo'
        const label = isCustomerDelivers ? t('orderForm.customerDeliverTime') : t('orderForm.pickupTime')
        return (
            <>
                <Col lg="3">
                    <DatePicker selected={job[fromField]}
                        label={`${label} (${t('orderForm.from')})`}
                        onChange={(date) => handleChange({ [fromField]: date })}
                        onSelect={(date) => handleSelectDate(fromField, date)}
                        showTimeSelect
                        timeIntervals={15}
                        timeCaption={t('common.time')}
                        minTime={setHours(setMinutes(new Date(), 0), 5)}
                        maxTime={setHours(setMinutes(new Date(), 59), 23)}
                        dateFormat="dd.MM.yyyy h:mm aa"
                        className="form-control mb-2"
                        required={job.pickupType === "PICKUP_FROM_HOME" || job.pickupType === "CUSTOMER_DELIVERS"}
                    />
                </Col>
                <Col lg="3">
                    <DatePicker selected={job[toField]}
                        label={`${label} (${t('orderForm.to')})`}
                        onChange={(date) => handleChange({ [toField]: date })}
                        onSelect={(date) => handleSelectDate(toField, date)}
                        showTimeSelect
                        timeIntervals={15}
                        timeCaption={t('common.time')}
                        minTime={setHours(setMinutes(new Date(), 0), 5)}
                        maxTime={setHours(setMinutes(new Date(), 59), 23)}
                        dateFormat="dd.MM.yyyy h:mm aa"
                        className="form-control mb-2"
                    />
                </Col>
            </>
        )
    }

    const fDelivery = () => (
        <>
            <Col lg="3">
                <DatePicker selected={job.completionTime}
                    label={t('orderForm.deliveryFrom')} placeholderText=''
                    onChange={(date) => handleChange({ completionTime: date })}
                    onSelect={(date) => handleSelectDate('completionTime', date)}
                    showTimeSelect
                    timeIntervals={15}
                    minTime={setHours(setMinutes(new Date(), 0), 5)}
                    maxTime={setHours(setMinutes(new Date(), 59), 23)}
                    timeCaption={t('common.time')}
                    dateFormat="dd.MM.yyyy h:mm aa"
                    className="form-control mb-2"
                />
            </Col>
            <Col lg="3">
                <DatePicker selected={job.completionTimeTo}
                    label={t('orderForm.deliveryTo')} placeholderText=''
                    onChange={(date) => handleChange({ completionTimeTo: date })}
                    onSelect={(date) => handleSelectDate('completionTimeTo', date)}
                    showTimeSelect
                    timeIntervals={15}
                    minTime={setHours(setMinutes(new Date(), 0), 5)}
                    maxTime={setHours(setMinutes(new Date(), 59), 23)}
                    timeCaption={t('common.time')}
                    dateFormat="dd.MM.yyyy h:mm aa"
                    className="form-control mb-2"
                />
            </Col>
        </>
    )

    /* avatar + name + role for the assignee selects (shown in the field and the menu) */
    const prettyRoles = (roles) => (roles ? String(roles).split(',').map(r => r.trim()).filter(Boolean).join(' · ') : '')
    const assigneeOptionLabel = (opt) => (
        <span className="asg-opt">
            <Avatar size={30} name={opt.label} src={opt.avatarUrl} />
            <span className="asg-opt-tx">
                <span className="asg-opt-nm">{opt.label}</span>
                {prettyRoles(opt.roles) && <span className="asg-opt-role">{prettyRoles(opt.roles)}</span>}
            </span>
        </span>
    )

    const fAssignees = () => (
        <>
            <Col lg="4">
                <Form.Group className="mb-2">
                    <AssigneeSelect
                        label={ASSIGNMENT_LABELS.pickup}
                        value={job.pickupAssignedToId ?? ''}
                        assigneesOptions={assigneesOptions}
                        filterRole={'driver'}
                        formatOptionLabel={assigneeOptionLabel}
                        onChange={(option) => handleChange({ pickupAssignedToId: option?.value })}
                        required={getStatusIndex(job.status) > getStatusIndex(JOB_STATUS.SCHEDULED) &&
                            getStatusIndex(job.status) <= getStatusIndex(JOB_STATUS.IN_PROGRESS) &&
                            job.pickupType === 'PICKUP_FROM_HOME'
                        }
                        isLoading={isLoadingAssigneesOptions}
                    />
                </Form.Group>
            </Col>
            <Col lg="4">
                <Form.Group className="mb-2">
                    <AssigneeSelect
                        label={ASSIGNMENT_LABELS.cleaning}
                        value={job.cleaningAssignedToId ?? ''}
                        assigneesOptions={assigneesOptions}
                        filterRole={'cleaner'}
                        formatOptionLabel={assigneeOptionLabel}
                        onChange={(option) => handleChange({ cleaningAssignedToId: option?.value })}
                        required={getStatusIndex(job.status) >= getStatusIndex(JOB_STATUS.IN_PROGRESS) &&
                            getStatusIndex(job.status) <= getStatusIndex(JOB_STATUS.FINISHING)}
                        isLoading={isLoadingAssigneesOptions}
                    />
                </Form.Group>
            </Col>
            <Col lg="4">
                <Form.Group className="mb-2">
                    <AssigneeSelect
                        label={ASSIGNMENT_LABELS.delivery}
                        assigneesOptions={assigneesOptions}
                        filterRole={'driver'}
                        value={job.deliveryAssignedToId ?? ''}
                        formatOptionLabel={assigneeOptionLabel}
                        onChange={(option) => handleChange({ deliveryAssignedToId: option?.value })}
                        required={getStatusIndex(job.status) >= getStatusIndex(JOB_STATUS.READY)}
                        isLoading={isLoadingAssigneesOptions}
                    />
                </Form.Group>
            </Col>
        </>
    )

    const fPayment = () => (
        <>
            <Col lg="3" style={{ marginRight: '24px', width: 'auto', flex: '0 0 auto' }}>
                <Form.Group className="mb-2">
                    <Form.Label>{t('orderForm.paymentStatus')}</Form.Label>
                    <div className="pay-toggle">
                        <button type="button" className={`pending${job.paymentStatus !== "PAID" ? ' on' : ''}`} onClick={() => handlePaymentStatusChange(false)}>{t('orderForm.pending')}</button>
                        <button type="button" className={`paid${job.paymentStatus === "PAID" ? ' on' : ''}`} onClick={() => handlePaymentStatusChange(true)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            {t('orderForm.paid')}
                        </button>
                    </div>
                </Form.Group>
            </Col>
            {job.paymentStatus === "PAID" &&
                <>
                    <Col lg="3">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('orderForm.paymentMethod')} <span className="text-danger">*</span></Form.Label>
                            <StyledSelect compact placeholder={t('styledSelect.placeholder')} value={job.paymentMethod ?? ""} onChange={(e) => handleChange({ paymentMethod: e.target.value })} required>
                                <option value="" disabled>{t('orderForm.selectPaymentMethod')}</option>
                                <option value="CASH">{t('orderForm.cash')}</option>
                                <option value="CREDIT_CARD">{t('orderForm.creditCard')}</option>
                                <option value="CHECK">{t('orderForm.check')}</option>
                            </StyledSelect>
                        </Form.Group>
                    </Col>
                    <Col lg="3">
                        <DatePicker selected={job.paymentTime}
                            label={t('orderForm.paymentTime')} placeholderText=''
                            onChange={(date) => handleChange({ paymentTime: date })}
                            onSelect={(date) => handleSelectDate('paymentTime', date)}
                            showTimeSelect
                            timeIntervals={15}
                            minTime={setHours(setMinutes(new Date(), 0), 5)}
                            maxTime={setHours(setMinutes(new Date(), 59), 23)}
                            timeCaption={t('common.time')}
                            dateFormat="dd.MM.yyyy h:mm aa"
                            className="form-control mb-2"
                            required
                        />
                    </Col>
                    <Col lg="3">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('orderForm.paymentType')} <span className="text-danger">*</span></Form.Label>
                            <StyledSelect compact placeholder={t('styledSelect.placeholder')} value={job.paymentType ?? ""} onChange={(e) => handleChange({ paymentType: e.target.value })} required>
                                <option value="" disabled>{t('orderForm.selectPaymentType')}</option>
                                <option value="BEFORE_SERVICE">{t('orderForm.beforeService')}</option>
                                <option value="DURING_SERVICE">{t('orderForm.duringService')}</option>
                                <option value="AFTER_SERVICE">{t('orderForm.afterService')}</option>
                            </StyledSelect>
                        </Form.Group>
                    </Col>
                </>
            }
        </>
    )

    const fNotes = () => (
        <Col lg="12" className="mb-3">
            <FormInput as="textarea" label={t('common.notes')} value={job.note} onChange={(e) => handleChange({ note: e.target.value })} />
        </Col>
    )

    const fLineItemsCard = () => (
        <Col xs="12">
            <div className="li-wrap">
                <div className="li-head">
                    <span className="t">{t('orderForm.lineItems')}</span>
                    <span className="rf" onClick={() => queryClient.invalidateQueries(['available-badges'])}>
                        <i className={`fa-solid fa-arrows-rotate ${isAvailableBadgesFetching ? 'fa-spin' : ''}`}></i> {t('orderForm.refreshTags')}
                    </span>
                </div>
                <div className="li-picker">
                    {job?.lineItemsTemp?.length > 0
                        ? job.lineItemsTemp.map((lineItem, index) => renderItemCard(lineItem, index))
                        : <p><i>{t('orderForm.noLineItemsFound')}</i></p>}
                    <button type="button" className="li-pick add" onClick={() => handleAddLineItem()}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        {t('orderForm.addService')}
                    </button>
                    <button type="button" className="li-pick add" onClick={() => handleAddMaterialLineItem()}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        </svg>
                        {t('orderForm.addMaterial')}
                    </button>
                </div>
                <p className="li-pick-hint">{t('orderForm.tapToEdit')}</p>
            </div>
        </Col>
    )

    /* ── mobile wizard: full inline line-item editor (no side pane) ── */
    const renderItemEditorInline = (lineItem, index) => {
        const open = detailIndex === index
        const bl = badgeLabel(lineItem.badgeId)
        const isMat = lineItem.type === 'MATERIAL'
        const name = isMat
            ? (lineItem.materialName || (lineItem.materialId ? t('orderForm.selectedMaterial') : t('orderForm.itemN', { n: index + 1 })))
            : (lineItem.serviceName || (lineItem.serviceId ? t('orderForm.selectedService') : t('orderForm.itemN', { n: index + 1 })))
        const hasId = isMat ? lineItem.materialId : lineItem.serviceId
        const meta = hasId
            ? `${rsdFormat(+lineItem.quantity, false)} ${lineItem.unit || ''}${bl ? ` · ${t('orderForm.tag')} ${bl}` : ''}`
            : (isMat ? t('orderForm.tapToChooseMaterial') : t('orderForm.tapToChooseService'))
        return (
        <div key={`m-li-${index}`} className={`m-li-acc${open ? ' open' : ''}`}>
            <button type="button" className="m-li-acc-head" onClick={() => (open ? closeItem() : openItem(index))}>
                <span className="m-li-acc-ic">{isMat ? <MaterialIcon /> : <ServiceIcon />}</span>
                <span className="m-li-acc-mid">
                    <span className={`m-li-acc-nm${hasId ? '' : ' ph'}`}>{name}</span>
                    <span className="m-li-acc-meta">{meta}</span>
                </span>
                <span className="m-li-acc-tot">{rsdFormat(+lineItem.priceWithTax, false)}</span>
                <span className="m-li-acc-chev">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </span>
            </button>
            {open &&
            <div className="m-li-acc-body">
            <div className="li-grid">
                <div className="li-col-2">
                    {isMat ? (
                        <Form.Group>
                            <Form.Label>{t('orderForm.material')} <span className="text-danger">*</span></Form.Label>
                            <MaterialSelect required value={lineItem.materialId ?? ''} onChange={(option) => handleMaterialSelect(option, index)} />
                        </Form.Group>
                    ) : (
                        <Form.Group>
                            <Form.Label>{t('orderForm.service')} <span className="text-danger">*</span></Form.Label>
                            <ServiceSelect required value={lineItem.serviceId ?? ''} onChange={(option) => handleServiceSelect(option, index)} />
                        </Form.Group>
                    )}
                </div>
                <div className="li-col-2">
                    <Form.Group>
                        <Form.Label>{t('orderForm.tagNumber')} {isCleaningAndOnwards(job) && <span className="text-danger">*</span>}</Form.Label>
                        <BadgeSelect required={isCleaningAndOnwards(job)} value={lineItem.badgeId ?? ''}
                            isLoading={isAvailableBadgesLoading}
                            options={sortBadgeOptions((availableBadges).filter(x => !selectedBadges.includes(x.value) || x.value === lineItem.badgeId))}
                            onChange={(option) => handleLineItemChange({ badgeId: option.value }, index)} />
                        {renderNextTagChip(lineItem, index)}
                    </Form.Group>
                </div>
                {lineItem.unit === 'm2' &&
                    <>
                        <div>
                            <FormInput value={lineItem.width ?? ''} label={t('orderForm.width')} type="number" min={0.01} step="0.01"
                                onChange={(e) => handleLineItemChange({ width: e.target.value }, index)}
                                required={isCleaningCompletedAndOnwards(job)} />
                        </div>
                        <div>
                            <FormInput value={lineItem.length ?? ''} label={t('orderForm.length')} type="number" min={0.01} step="0.01"
                                onChange={(e) => handleLineItemChange({ length: e.target.value }, index)}
                                required={isCleaningCompletedAndOnwards(job)} />
                        </div>
                    </>
                }
                <div>
                    <FormInput value={lineItem.quantity} title={rsdFormat(+lineItem.quantity, false)}
                        label={(lineItem.serviceId || lineItem.materialId) ? lineItem.unit : t('orderForm.quantity')}
                        type="number" min={getLineItemMinQuantity(lineItem.unit)} step={lineItem.unit === 'Piece' ? 1 : 0.01}
                        onChange={(e) => handleLineItemChange({ quantity: e.target.value }, index)}
                        disabled={lineItem.unit === 'm2'} required />
                </div>
                <div>
                    <FormInput value={lineItem.taxRate} label={t('orderForm.taxPercent')} type="number" step='0.01'
                        onChange={(e) => handleLineItemChange({ taxRate: e.target.value }, index)} />
                </div>
                <div>
                    <FormInput disabled value={lineItem.price} title={rsdFormat(+lineItem.price, false)}
                        label={t('orderForm.price')} type="number" min="0.01" step="0.01"
                        onChange={(e) => handleLineItemChange({ price: e.target.value }, index)} required />
                </div>
                <div>
                    <FormInput disabled value={lineItem.priceWithTax} title={rsdFormat(+lineItem.priceWithTax, false)}
                        label={t('orderForm.total')} type="number" min="0.01" step="0.01" required
                        onChange={(e) => handleLineItemChange({ priceWithTax: e.target.value }, index)} />
                </div>
            </div>
            <div className="m-li-acc-foot">
                <div className="m-li-total"><span>{t('orderForm.lineTotal')}</span><b>{rsdFormat(+lineItem.priceWithTax, false)} RSD</b></div>
                <button type="button" className="m-li-remove" onClick={() => handleRemoveLineItem(index)}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                    {t('orderForm.removeItem')}
                </button>
            </div>
            </div>
            }
        </div>
        )
    }

    /* Step 5 — schedule & payment, as polished cards + a totals breakdown */
    const renderSchedulePayment = () => {
        const totalNum = (job.lineItemsTemp || []).reduce((a, o) => a + (parseFloat(o.priceWithTax) || 0), 0)
        const paidNum = job.paymentStatus === 'PAID' ? totalNum : 0
        const remainingNum = totalNum - paidNum
        return (
            <div className="msp">
                <div className="msp-card">
                    <div className="msp-h"><span className="msp-ic ic-blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg></span>{t('orderForm.schedule')}</div>
                    <Row>{fDelivery()}</Row>
                </div>

                <div className="msp-card">
                    <div className="msp-h"><span className="msp-ic ic-green"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg></span>{t('orderForm.payment')}</div>
                    <Row>{fPayment()}</Row>
                </div>

                <div className="msp-tot">
                    <div className="msp-tot-row"><span>{t('orderForm.total')}</span><b>{rsdFormat(totalNum)}</b></div>
                    <div className="msp-tot-row"><span>{t('orderForm.paid')}</span><b className={paidNum > 0 ? 'g' : ''}>{rsdFormat(paidNum)}</b></div>
                    <div className="msp-tot-row grand"><span>{t('orderForm.remaining')}</span><b className={remainingNum > 0 ? 'r' : 'g'}>{rsdFormat(remainingNum)}</b></div>
                </div>

                <div className="msp-note">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                    {t('orderForm.invoiceNote')}
                </div>
            </div>
        )
    }

    /* Step 6 — review dashboard (mockup: "show complete job dashboard, not just a summary") */
    const renderReview = () => {
        const c = clientStats?.client
        const isCompany = c?.clientType === 'COMPANY'
        const contactName = c ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() : ''
        const clientName = c ? (isCompany ? (c.companyName || contactName) : contactName) : (clientRawName || t('orderForm.noClientSelected'))
        const staffIds = [job.pickupAssignedToId, job.cleaningAssignedToId, job.deliveryAssignedToId].filter(Boolean)
        const staffPeople = staffIds.map((id) => assigneesOptions.find((o) => o.value === id)).filter(Boolean)
        const sIdx = getStatusIndex(job.status)
        const paid = job.paymentStatus === 'PAID'
        const pickupT = job.pickupType === 'CUSTOMER_DELIVERS' ? job.deliveredByCustomerTime : job.pickupTime
        return (
            <div className="mrev">
                <div className="mrev-card cust">
                    <Avatar square size={44} name={clientName} src={c?.avatarUrl} />
                    <div className="mrev-cust-tx">
                        <div className="t">{clientName}</div>
                        <div className="s">{[isCompany && contactName ? `${t('orderForm.contact')} ${contactName}` : null, c?.phone, c?.city].filter(Boolean).join(' · ') || (c ? (isCompany ? t('orderForm.company') : t('orderForm.individual')) : t('orderForm.client'))}</div>
                    </div>
                    <span className="mrev-tag">{job.clientId ? t('orderForm.selected') : t('orderForm.missing')}</span>
                </div>

                <div className="mrev-grid">
                    <div className="mrev-card">
                        <div className="mrev-h"><span className="mrev-ic ic-blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg></span>{t('common.status')}</div>
                        <div className="mrev-v">{getStatusLabel(job.status)}</div>
                        <div className="mrev-sub">{sIdx >= 0 ? t('orderForm.stepNof8', { n: sIdx + 1 }) : ''}</div>
                    </div>
                    <div className="mrev-card">
                        <div className="mrev-h"><span className="mrev-ic ic-purple"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg></span>{t('orderForm.services')}</div>
                        <div className="mrev-v">{t('orderForm.itemCount', { count: job.lineItemsTemp?.length || 0 })}</div>
                        <div className="mrev-sub">{getTotalPrice()}</div>
                    </div>
                    <div className="mrev-card">
                        <div className="mrev-h"><span className="mrev-ic ic-green"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.9" /></svg></span>{t('orderForm.staff')}</div>
                        <div className="mrev-stack">
                            {staffPeople.length ? staffPeople.map((p, i) => <Avatar key={i} size={26} name={p.label} src={p.avatarUrl} />) : <span className="mrev-sub">{t('orderForm.noneYet')}</span>}
                        </div>
                        <div className="mrev-sub">{t('orderForm.nOfAssigned', { n: staffIds.length })}</div>
                    </div>
                    <div className="mrev-card">
                        <div className="mrev-h"><span className="mrev-ic ic-amber"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg></span>{t('orderForm.pickup')}</div>
                        <div className="mrev-v" style={{ fontSize: 13 }}>{PICKUP_LBL[job.pickupType] || '—'}</div>
                        <div className="mrev-sub">{pickupT ? toLocalDateString(pickupT) : t('orderForm.noTimeSet')}</div>
                    </div>
                    <div className="mrev-card span2">
                        <div className="mrev-h"><span className="mrev-ic ic-blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg></span>{t('orderForm.schedule')}</div>
                        <div className="mrev-line"><span>{t('orderForm.delivery')}</span><b>{job.completionTime ? toLocalDateString(job.completionTime) : '—'}</b></div>
                    </div>
                    <div className="mrev-card span2">
                        <div className="mrev-h"><span className="mrev-ic ic-rose"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg></span>{t('orderForm.payment')}<span className={`mrev-pill ${paid ? 'paid' : 'pending'}`}>{paid ? t('orderForm.paid') : t('orderForm.pending')}</span></div>
                        <div className="mrev-line"><span>{paid ? t('orderForm.paid') : t('orderForm.due')}</span><b>{getTotalPrice()}</b></div>
                    </div>
                </div>

                <Row className="mb-1 mt-1">{fNotes()}</Row>
            </div>
        )
    }

    /* ── mobile wizard step body ── */
    const renderWizardStep = () => {
        if (wizStep === 0) return <Row className="mb-3">{fClient()}{renderClientSummary()}{fPickupType()}{fPickupTime()}</Row>
        if (wizStep === 1) return (
            <div className="m-items">
                <div className="li-head">
                    <span className="t">{t('orderForm.lineItems')}</span>
                    <span className="rf" onClick={() => queryClient.invalidateQueries(['available-badges'])}>
                        <i className={`fa-solid fa-arrows-rotate ${isAvailableBadgesFetching ? 'fa-spin' : ''}`}></i> {t('orderForm.refreshTags')}
                    </span>
                </div>
                {job?.lineItemsTemp?.length > 0
                    ? job.lineItemsTemp.map((lineItem, index) => renderItemEditorInline(lineItem, index))
                    : <p><i>{t('orderForm.noLineItemsFound')}</i></p>}
                <button type="button" className="li-pick add" onClick={() => handleAddLineItem()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    {t('orderForm.addService')}
                </button>
                <button type="button" className="li-pick add" onClick={() => handleAddMaterialLineItem()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    </svg>
                    {t('orderForm.addMaterial')}
                </button>
                <div className="m-items-total">
                    <span>{t('orderForm.itemsTotal', { count: job.lineItemsTemp?.length || 0 })}</span>
                    <b>{getTotalPrice()}</b>
                </div>
            </div>
        )
        if (wizStep === 2) return (
            <>
                <Form.Label>{t('common.status')} <span className="text-danger">*</span></Form.Label>
                <StatusSelector value={job.status} onChange={(status) => handleChange({ status: status })} />
            </>
        )
        if (wizStep === 3) return <Row className="mb-3">{fAssignees()}</Row>
        if (wizStep === 4) return renderSchedulePayment()
        return renderReview()
    }

    /* ── footer: wizard nav on mobile, standard actions on desktop ── */
    const footerNode = isMobile ? (
        <div className="m-wiz-foot">
            {wizStep > 0
                ? <button key="wiz-back" type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => goWiz(-1)}>{t('common.back')}</button>
                : <button key="wiz-cancel" type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide}>{t('common.cancel')}</button>}
            {wizStep < WIZ_STEPS.length - 1
                ? <button key="wiz-next" type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => goWiz(1)}>{t('orderForm.continue')}</button>
                : <button key="wiz-submit" type="submit" form="set-job" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                    {isLoading ? t('orderForm.submitting') : t('orderForm.createJob')}
                </button>}
        </div>
    ) : (
        <>
            <div className="drawer-total">
                <span className="l">{t('orderForm.total')}</span>
                <span className="v">{getTotalPrice()}</span>
            </div>
            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide}>{t('common.cancel')}</button>
            <button type='submit' form="set-job" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                {isLoading ? t('orderForm.submitting') : t('orderForm.createJob')}
            </button>
        </>
    )

    return (
        <>
            <SetClientModal display={setClientModal} toggle={setSetClientModal} onCreateClient={onCreateClient} suggestName={''} asDrawer />
            <ClientInfoModal display={clientInfoModal} toggle={setClientInfoModal} clientId={job.clientId} onViewJobs={onHide} />

            <SbrDrawer
                show={display}
                onHide={onHide}
                onShow={onShow}
                className={`job-drawer${isMobile ? ' job-drawer-wiz' : ''}`}
                title={t('orderForm.createNewJob')}
                subtitle={t('orderForm.scheduleSubtitle')}
                icon={<BoxIcon />}
                footer={footerNode}
            >
                        <Form id='set-job' onSubmit={handleSubmit}>
                        {isMobile ? (
                            <div className="m-wiz">
                                <div className="m-wiz-bar">
                                    {WIZ_STEPS.map((s, i) => <span key={i} className={`m-wiz-seg${i < wizStep ? ' done' : ''}${i === wizStep ? ' on' : ''}`} />)}
                                </div>
                                <div className="m-wiz-cap">{t('orderForm.stepNofTotal', { step: wizStep + 1, total: WIZ_STEPS.length })}</div>
                                <h2 className="m-wiz-h">{WIZ_STEPS[wizStep]}</h2>
                                {renderWizardStep()}
                            </div>
                        ) : (
                            <div className="job-split">
                                <div className="job-split-left">
                                    <Row className="mb-3 g-4">
                                        {fClient()}{renderClientSummary()}{fPickupType()}{fPickupTime()}{fDelivery()}{fAssignees()}{fPayment()}{fNotes()}{fLineItemsCard()}
                                    </Row>
                                </div>
                                <div className="job-split-right">
                                    {detailIndex == null || !job.lineItemsTemp?.[detailIndex] ? renderSummary() : renderItemEditor(detailIndex)}
                                </div>
                            </div>
                        )}

                        {(isLoadingAssigneesOptions || isAvailableBadgesLoading) &&
                            <OverlayLoader />
                        }
                        </Form>
            </SbrDrawer>

        </>
    )
}

export default SetOrderModal